/**
 * What a placed bet is actually waiting on.
 *
 * The ledger has shown PLACED for two completely different situations: a bet
 * whose game starts in three hours, and a bet whose game finished six hours
 * ago and was never graded. They look identical, which is why "are losses
 * settling?" could not be answered by looking at the board and took an hour of
 * queries to disprove.
 *
 * Awaiting is the one that needs an age attached. A bet sitting unresolved
 * four hours after a nine-inning game should be visible as a number, not
 * inferred from the absence of a result.
 */
export type PlacedBetPhase =
  "SCHEDULED" | "IN_PLAY" | "AWAITING_RESULT" | "SETTLED" | "VOID";

/**
 * How long after first pitch a game is assumed to be over.
 *
 * Nine innings run about three hours. Past that a bet with no result is not
 * "in play", it is unresolved - the same threshold the settlement sweep uses
 * to decide a game should have been gradeable.
 */
export const GAME_ASSUMED_OVER_MS = 3 * 60 * 60_000;

export interface PlacedBetProgress {
  readonly phase: PlacedBetPhase;
  /** Hours since the game was assumed finished. Null unless awaiting. */
  readonly awaitingHours: number | null;
}

export function placedBetProgress(
  bet: {
    readonly status: "PLACED" | "SETTLED" | "VOID";
    readonly result: string | null;
    readonly gameStartsAt: string | null;
  },
  now: Date,
): PlacedBetProgress {
  if (bet.status === "VOID") return { phase: "VOID", awaitingHours: null };
  if (bet.status === "SETTLED" || bet.result !== null)
    return { phase: "SETTLED", awaitingHours: null };

  const start = bet.gameStartsAt === null ? NaN : Date.parse(bet.gameStartsAt);
  // Without a start time there is nothing to measure against, so claiming the
  // bet is late would be a guess. Left as scheduled rather than invented.
  if (!Number.isFinite(start))
    return { phase: "SCHEDULED", awaitingHours: null };

  const elapsed = now.getTime() - start;
  if (elapsed < 0) return { phase: "SCHEDULED", awaitingHours: null };
  if (elapsed < GAME_ASSUMED_OVER_MS)
    return { phase: "IN_PLAY", awaitingHours: null };
  return {
    phase: "AWAITING_RESULT",
    awaitingHours: (elapsed - GAME_ASSUMED_OVER_MS) / 3_600_000,
  };
}

export function describePlacedBetPhase(progress: PlacedBetProgress): string {
  switch (progress.phase) {
    case "SCHEDULED":
      return "Scheduled";
    case "IN_PLAY":
      return "In play";
    case "AWAITING_RESULT":
      return `Awaiting result (${Math.floor(progress.awaitingHours ?? 0)}h)`;
    case "VOID":
      return "Void";
    default:
      return "Settled";
  }
}
